/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { ccFields, CCFieldType } from "@protontech/autofill/types";
import type { Fnode } from "@protontech/fathom";
import { not, or } from "./combinators";
import { getLabelFor } from "./dom";
import { getAutocompletes } from "./extract";
import { getCachedSubType, isClassifiable, setCachedSubType } from "./flags";
import { matchCCExp, matchCCExpMonth, matchCCExpYear, matchCCFirstName, matchCCLastName, matchCCName, matchCCNumber, matchCCOutlier, matchCCSecurityCode, matchCCV } from "./re";
import { sanitizeString, sanitizeStringWithSpaces } from "./text";
import { isVisible } from "./visible";

export type CCFieldElement = HTMLInputElement | HTMLSelectElement;
export type CCExpirationMonthFormat = { padding: boolean };
export type CCExpirationYearFormat = { fullYear: boolean };
export type CCExpirationFormat = { separator: string; fullYear: boolean; monthFirst: boolean };

type CCFieldPredicate = (field: HTMLInputElement | HTMLSelectElement, autocompletes: string[], haystack: string) => boolean;
type CCFieldConfig = [CCFieldType, CCFieldPredicate];
type CCFieldMatchParams = { visible: boolean };

export const CC_ATTRIBUTES = ["autocomplete", "name", "id", "class", "form", "aria-label", "aria-labelledby", "placeholder", "data-testid", "data-stripe", "data-recurly", "data-encrypted-name"];
/* `.type` normalizes unknown type attributes (eg "phone") to "text" */
export const CC_INPUT_TYPES = ["tel", "text", "number", "password"];

/** Order by longest first to prevent false positives */
const CC_EXP_YEAR_FORMAT = ["YYYY", "AAAA", "YY", "AA"]; // 4-digit before 2-digit
const CC_EXP_MONTH_FORMAT = ["MM", "LL"]; // 4-digit before 2-digit
const CC_EXP_FULL_RE = /(mmyy|mmaa|yymm|aamm)/;
const CC_EXP_SEPARATOR = ["", "/", "-"];
const CC_EXP_DEFAULT_FORMAT: CCExpirationFormat = { separator: "/", fullYear: false, monthFirst: true };
const CC_EXP_FORMATS: Omit<CCExpirationFormat, "separator">[] = [
  { fullYear: true, monthFirst: true },
  { fullYear: false, monthFirst: true },
  { fullYear: true, monthFirst: false },
  { fullYear: false, monthFirst: false },
];

const MAX_LENGTH_FORMAT_MAP: Record<number, CCExpirationFormat> = {
  4: { separator: "", fullYear: false, monthFirst: true },
  5: { separator: "/", fullYear: false, monthFirst: true },
  6: { separator: "", fullYear: true, monthFirst: true },
  7: { separator: "/", fullYear: true, monthFirst: true },
};

const YEAR_OPTION = (new Date().getFullYear() + 1).toString();

/** Generate a dummy date string to test against an input's
 * regexp pattern in order to infer the cc expiration format */
const generateExpirationString = ({ fullYear, separator, monthFirst }: CCExpirationFormat): string => {
  const month = "01";
  const year = fullYear ? "2025" : "25";
  return monthFirst ? `${month}${separator}${year}` : `${year}${separator}${month}`;
};

/** Attempts to determine expiration format by testing the input's pattern attribute
 * with various date combinations to see which format the regex accepts */
const getExpirationFormatFromPattern = (pattern: string): CCExpirationFormat | undefined => {
  try {
    const regex = new RegExp(`^${pattern}$`); // nosemgrep: gitlab.eslint.detect-non-literal-regexp, javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp

    for (const format of CC_EXP_FORMATS) {
      for (const separator of CC_EXP_SEPARATOR) {
        const testString = generateExpirationString({ ...format, separator });
        if (regex.test(testString)) {
          return {
            separator,
            fullYear: format.fullYear,
            monthFirst: format.monthFirst,
          };
        }
      }
    }
  } catch {
    return undefined;
  }
};

const getCCFormatHaystack = (input: HTMLInputElement): string => {
  const { placeholder, className, id, name } = input;
  const ariaLabel = input.getAttribute("aria-label");
  const label = getLabelFor(input)?.innerText;
  return [className, id, name, ariaLabel, placeholder.replace(/\s/g, ""), label?.replace(/\s/g, "")].filter(Boolean).join(" ").toUpperCase();
};

/** Infers format from maxLength attribute using common patterns */
const getExpirationFormatFromMaxLength = (maxLength: number): CCExpirationFormat | undefined => MAX_LENGTH_FORMAT_MAP[maxLength];

/** Searches input attributes (placeholder, className, id, name) for format patterns
 * like "MM/YYYY", "YY-MM", etc. to determine the expected format */
const getExpirationFormatFromAttributes = (input: HTMLInputElement): CCExpirationFormat | undefined => {
  const haystack = getCCFormatHaystack(input);

  for (const separator of CC_EXP_SEPARATOR) {
    for (const year of CC_EXP_YEAR_FORMAT) {
      for (const monthFirst of [true, false]) {
        if (haystack.includes(monthFirst ? `MM${separator}${year}` : `${year}${separator}MM`)) {
          return {
            separator,
            fullYear: year.length === 4,
            monthFirst,
          };
        }
      }
    }
  }
};

/** Determines a full credit card expiration format from an input element.
 * Uses multiple detection methods in order of reliability:
 * 1. Text attributes (most explicit)
 * 2. Pattern attribute (most precise)
 * 3. MaxLength attribute (common hint)
 * 4. Default format (fallback) */
export const getExpirationFormat = (field: HTMLElement, allowFallback = true): CCExpirationFormat | undefined => {
  if (field instanceof HTMLInputElement) {
    const validMaxLength = field.maxLength >= 4;
    const validPattern = field.pattern && (field.maxLength === -1 || validMaxLength);

    return (
      getExpirationFormatFromAttributes(field) ||
      (validPattern && getExpirationFormatFromPattern(field.pattern)) ||
      (validMaxLength && getExpirationFormatFromMaxLength(field.maxLength)) ||
      (allowFallback ? CC_EXP_DEFAULT_FORMAT : undefined)
    );
  }
};

export const formatExpirationDate = (month: string, year: string, { fullYear, separator, monthFirst }: CCExpirationFormat): string => {
  const formattedYear = fullYear ? year.slice(0, 4) : year.slice(-2);
  const formattedMonth = month.padStart(2, "0");
  const components = monthFirst ? [formattedMonth, formattedYear] : [formattedYear, formattedMonth];

  return components.join(separator);
};

const getSelectOptions = (el: HTMLSelectElement) =>
  Array.from(el.options)
    .map((opt) => opt.value)
    .filter(Boolean);

export const getInputExpirationMonthFormat = (input: HTMLInputElement): CCExpirationMonthFormat => {
  const haystack = getCCFormatHaystack(input);
  if (CC_EXP_MONTH_FORMAT.some((pattern) => haystack.includes(pattern))) return { padding: true };
  return { padding: input.minLength === 2 || input.maxLength === 2 };
};

export const getInputExpirationYearFormat = (input: HTMLInputElement): CCExpirationYearFormat => {
  if (input.minLength === 4) return { fullYear: true };

  const haystack = getCCFormatHaystack(input);
  for (const year of CC_EXP_YEAR_FORMAT) {
    if (haystack.includes(year)) return { fullYear: year.length === 4 };
  }

  return { fullYear: false };
};

export const getSelectExpirationYearFormat = (select: HTMLSelectElement): CCExpirationYearFormat | undefined => {
  const options = getSelectOptions(select);
  if (options.some((value) => /^\d{4}$/.test(value))) return { fullYear: true };
  if (options.some((value) => /^\d{2}$/.test(value))) return { fullYear: false };
};

export const getSelectExpirationMonthFormat = (select: HTMLSelectElement): CCExpirationMonthFormat | undefined => {
  const options = getSelectOptions(select);
  if (options.some((value) => /^0[1-9]$/.test(value))) return { padding: true };
  if (options.some((value) => /^([1-9]|1[0-2])$/.test(value))) return { padding: false };
};

/** Identity autocomplete values that should be excluded from CC field detection */
const AUTOCOMPLETE_OUTLIERS = ["given-name", "additional-name", "family-name", "name"];

const guard =
  (options: { autocomplete: string; password?: boolean }, predicate: CCFieldPredicate): CCFieldPredicate =>
  (field, autocompletes, haystack): boolean => {
    if (!options.password && field.getAttribute("type") === "password") return false;
    if (autocompletes.includes(options.autocomplete)) return true;
    if (autocompletes.some((autocomplete) => AUTOCOMPLETE_OUTLIERS.includes(autocomplete))) return false;
    return predicate(field, autocompletes, haystack) && not(matchCCOutlier)(haystack);
  };

const notCCIdentityOutlier = (autocompletes: string[]) => {
  if (autocompletes.includes("billing")) return false;
  if (autocompletes.includes("shipping")) return false;
  return true;
};

const isCCFirstName: CCFieldPredicate = (_, autocompletes, haystack) => matchCCFirstName(haystack) && notCCIdentityOutlier(autocompletes);
const isCCLastName: CCFieldPredicate = (_, autocompletes, haystack) => matchCCLastName(haystack) && notCCIdentityOutlier(autocompletes);
const isCCName: CCFieldPredicate = (_, autocomplete, haystack) => matchCCName(haystack) && notCCIdentityOutlier(autocomplete);
const isCCSecurityCode: CCFieldPredicate = (_, _auto, haystack) => or(matchCCV, matchCCSecurityCode)(haystack);
const isCCNumber: CCFieldPredicate = (_, __, haystack) => matchCCNumber(haystack);
const isCCExp: CCFieldPredicate = (field, _, haystack) => field instanceof HTMLInputElement && matchCCExp(haystack);

const isCCExpMonth: CCFieldPredicate = (field, autocompletes, haystack) => {
  if (autocompletes.includes("cc-exp")) return false;
  if (CC_EXP_FULL_RE.test(haystack)) return false;
  if (matchCCExpMonth(haystack)) return true;

  if (field instanceof HTMLSelectElement && matchCCExp(haystack)) {
    /** min 12months options - lenient for placeholder values */
    return field.options.length >= 12 && field.options.length <= 14;
  }

  return false;
};

const isCCExpYear: CCFieldPredicate = (field, autocompletes, haystack) => {
  if (autocompletes.includes("cc-exp")) return false;
  if (CC_EXP_FULL_RE.test(haystack)) return false;
  if (matchCCExpYear(haystack)) return true;

  if (field instanceof HTMLSelectElement && matchCCExp(haystack)) {
    for (const option of field.options) {
      if (option.innerText === YEAR_OPTION) return true;
      if (option.value === YEAR_OPTION) return true;
    }
  }

  return false;
};

const CC_MATCHERS: CCFieldConfig[] = [
  [CCFieldType.FIRSTNAME, guard({ autocomplete: "cc-given-name" }, isCCFirstName)],
  [CCFieldType.LASTNAME, guard({ autocomplete: "cc-family-name" }, isCCLastName)],
  [CCFieldType.NAME, guard({ autocomplete: "cc-name" }, isCCName)],

  [CCFieldType.EXP_YEAR, guard({ autocomplete: "cc-exp-year" }, isCCExpYear)],
  [CCFieldType.EXP_MONTH, guard({ autocomplete: "cc-exp-month" }, isCCExpMonth)],
  [CCFieldType.EXP, guard({ autocomplete: "cc-exp" }, isCCExp)],

  [CCFieldType.CSC, guard({ autocomplete: "cc-csc", password: true }, isCCSecurityCode)],
  [CCFieldType.NUMBER, guard({ autocomplete: "cc-number", password: true }, isCCNumber)],
];

export const getCCHaystack = (field: HTMLElement): string => {
  const attrs = CC_ATTRIBUTES.map((attr) => field?.getAttribute(attr) ?? "");
  const labelEl = getLabelFor(field);

  /** Labels using pseudo-elements (::before/::after) may have empty
   * `innerText`. Fall back to extracting text from child elements. */
  const label = sanitizeString(
    (() => {
      if (!labelEl) return "";
      if (labelEl.innerText) return labelEl.innerText;
      if (labelEl.childElementCount === 0) return "";
      return Array.from(labelEl.children)
        .map((el) => (el as HTMLElement)?.innerText ?? "")
        .join(" ");
    })(),
  );

  return sanitizeStringWithSpaces(attrs.join(" ")) + " " + label;
};

export const getCachedCCSubtype = (el: HTMLElement): CCFieldType | undefined => {
  const subType = getCachedSubType(el);
  if (subType && ccFields.has(subType)) return subType as CCFieldType;
};

/** May be run during form classification, field feature vector creation
 * or when consuming detection results. As such, leverage cache as much
 * as possible to avoid re-computing. */
export const getCCFieldType = (field: CCFieldElement): CCFieldType | undefined => {
  const cachedSubType = getCachedCCSubtype(field);
  if (cachedSubType) return cachedSubType;

  if (field.tagName === "INPUT" && !CC_INPUT_TYPES.includes(field.type)) return;

  const haystack = getCCHaystack(field);
  const autocompletes = getAutocompletes(field);

  if (haystack) {
    const ccType = CC_MATCHERS.find(([, test]) => test(field, autocompletes, haystack))?.[0];
    if (ccType) setCachedSubType(field, ccType);
    return ccType;
  }
};

/** Matches against a potential `field` trainee candidate which must be
 * classifiable. Should not be used outside of feature computation. */
export const matchCCFieldCandidate = (input: HTMLInputElement, { visible }: CCFieldMatchParams): boolean => {
  if (getCachedCCSubtype(input)) return true;
  if (!visible) return false;

  const ccType = getCCFieldType(input);
  if (ccType) setCachedSubType(input, ccType);
  return ccType !== undefined;
};

/** Checks if a given Fnode might represent a select element identity field.
 * NOTE: Select elements do not have a field feature vector as such we have
 * to ensure it is visible & classifiable in order to be a valid candidate. */
export const isCCSelectField = (fnode: Fnode): boolean => {
  const select = fnode.element;
  if (!(select instanceof HTMLSelectElement)) return false;

  if (getCachedCCSubtype(select)) return true;

  /** Select elements may be opaque or hidden in certain components, only
   * check for opacity visibility for input fields */
  const visible = isVisible(select, { opacity: false });
  if (!visible) return false;

  const isProcessable = isClassifiable(select);
  if (!isProcessable) return false;

  const ccType = getCCFieldType(select);
  if (ccType) setCachedSubType(select, ccType);

  return ccType !== undefined;
};
